import React from 'react'

import {
  BaseContent,
  Button,
  Heading,
  Skeleton,
  useMediaQueryContext,
  useSlider,
} from '@fourthwall/components'
import { useWebViewBridge } from '@fourthwall/webview-bridge'
import { useOffersTotal } from '@hooks'
import { routing } from '@utils/routing'

import { useCreateProduct } from '../../../Product/hooks/useCreateProduct'
import { useCampaignStatus } from '../../Recommendations/hooks/useCampaignStatus'
import { Bonus } from '../components/Bonus/Bonus'
import { Pills } from '../components/Pills'
import { Slider } from '../components/Slider'
import { useHomeProducts } from '../useHomeProducts'
import * as S from './styled'

const getCopyProps = (count: number | undefined) => {
  // no products
  if (count === 0 || count === undefined) {
    return {
      title: 'Create your first product',
      subtitle: 'Design a new product or list something you own.',
    }
  }

  // 1 to 4 products created
  if (count <= 4) {
    return {
      title: 'Create another product',
      subtitle: 'We recommend giving supporters 3-5 options to choose from.',
    }
  }

  // > 4 products created
  return {
    title: 'Create another product',
    subtitle: 'Give supporters more options to choose from.',
  }
}

export const HomeProductsSlider = () => {
  const offersTotal = useOffersTotal()
  const mediaQuery = useMediaQueryContext()
  const { openCreateProductModal } = useCreateProduct()
  const homeProducts = useHomeProducts()
  const { methods, isWebView } = useWebViewBridge()
  const slider = useSlider()
  const { activeIncentives } = useCampaignStatus()
  const incentive = activeIncentives.find((i) => i.recommendationId === 'createProduct')

  // if (!offersTotal) return null
  const count = offersTotal?.all

  const renderButtons = () => {
    return (
      <S.ButtonGroup>
        <Button
          label="Browse all products"
          appearance="secondary"
          size="xsmall"
          fullWidth
          {...(isWebView
            ? {
                onClick: async () =>
                  methods.openMobilePage?.({
                    mobile_path: 'product_catalog',
                    url: routing.productCatalog.self,
                  }),
              }
            : { href: routing.productCatalog.self })}
          analytics="browseAllProducts"
        />
        <Button
          label="Create product"
          appearance="primary"
          size="xsmall"
          fullWidth
          onClick={async () => {
            if (isWebView) {
              await methods.openMobilePage?.({
                mobile_path: 'product_catalog',
                url: routing.productCatalog.self,
              })
            } else {
              openCreateProductModal()
            }
          }}
          analytics="createANewProduct"
        />
      </S.ButtonGroup>
    )
  }

  if (!homeProducts || homeProducts?.isLoading) {
    return <RecommendationSkeleton />
  }

  if (!homeProducts) {
    return null
  }

  return (
    <S.Container>
      <S.Header>
        <div>
          {mediaQuery.tablet ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Heading type="h4" weight="semibold" asTag="h3">
                {getCopyProps(count).title}
              </Heading>
              {incentive && <Bonus bonus={incentive.incentive.bonus.value} />}
            </div>
          ) : (
            <BaseContent>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <strong> {getCopyProps(count).title}</strong>
                {incentive && <Bonus bonus={incentive.incentive.bonus.value} />}
              </div>
            </BaseContent>
          )}
        </div>

        {!homeProducts?.categories.visible && renderButtons()}
      </S.Header>
      <div>
        {homeProducts?.categories &&
          (() => {
            const { $slidesCount, ...containerProps } = slider.getContainerProps()

            return (
              <div {...containerProps}>
                <S.SliderHeader $marginTop>
                  {homeProducts.categories &&
                    homeProducts.categories.visible &&
                    mediaQuery.tablet && (
                      <Pills>
                        {homeProducts.categories.options.map((option) => {
                          return (
                            <Pills.Pill
                              key={option.value}
                              active={option.value === homeProducts.categories.value}
                              label={option.label}
                              onClick={() => homeProducts?.categories.onChange(option.value)}
                            />
                          )
                        })}
                      </Pills>
                    )}

                  {homeProducts.categories.visible && renderButtons()}
                </S.SliderHeader>

                <Slider
                  products={homeProducts.products}
                  activeCategory={homeProducts.activeCategory}
                  slider={slider}
                  onProductClick={homeProducts.onProductClick}
                />
              </div>
            )
          })()}
      </div>
    </S.Container>
  )
}

const RecommendationSkeleton = () => {
  const mediaQuery = useMediaQueryContext()

  const skeletonCount = (() => {
    if (mediaQuery.tablet) return 5

    return 2
  })()

  return (
    <S.Container>
      <S.Header>
        <div>
          {mediaQuery.tablet ? (
            <Skeleton.Shape height={32} width={250} />
          ) : (
            <Skeleton.Shape height={24} width={200} />
          )}
        </div>
        <S.ButtonGroup>
          <Skeleton.Shape height={36} width="100%" />
          <Skeleton.Shape height={36} width="100%" />
        </S.ButtonGroup>
      </S.Header>
      <S.SkeletonSlider>
        {[...Array(skeletonCount)].map((_, index) => (
          <S.SkeletonCard key={index}>
            <S.SkeletonProductImage />
            <S.SkeletonProductInfo>
              <Skeleton.Shape height={20} width="80%" />
              <Skeleton.Shape height={16} width="40%" />
            </S.SkeletonProductInfo>
          </S.SkeletonCard>
        ))}
      </S.SkeletonSlider>
    </S.Container>
  )
}
